import { type ReactNode, useEffect, useRef, useState } from 'react';

import { cn } from './cn.js';

const GRAIN_BG =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")";

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [isRevealed, setIsRevealed] = useState(prefersReducedMotion);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, isRevealed };
}

export function GrainOverlay() {
  return (
    <div
      className="pointer-events-none fixed inset-0 z-[9999] bg-repeat opacity-[0.03]"
      style={{ backgroundImage: GRAIN_BG }}
      aria-hidden="true"
    />
  );
}

export function ScrollReveal({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { ref, isRevealed } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={cn(
        'translate-y-[30px] opacity-0 transition-all duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
        isRevealed && 'translate-y-0 opacity-100',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Container({
  children,
  className,
  narrow,
}: {
  children: ReactNode;
  className?: string;
  narrow?: boolean;
}) {
  return (
    <div
      className={cn(
        'mx-auto px-8',
        narrow ? 'max-w-[900px]' : 'max-w-[1400px]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SectionHeader({
  number,
  title,
  subtitle,
  className,
}: {
  number: string;
  title: string;
  subtitle?: string;
  className?: string;
}) {
  return (
    <div className={cn('mb-8', className)}>
      <span className="text-muted-foreground mb-2 block font-mono text-[0.625rem] font-medium tracking-[0.05em] uppercase">
        {number}
      </span>
      <h2 className="font-heading text-foreground text-[clamp(1.75rem,4vw,2.5rem)] leading-[1.2] font-normal">
        {title}
      </h2>
      {subtitle && (
        <p className="text-secondary-foreground mt-4 max-w-[55ch] text-base leading-relaxed">
          {subtitle}
        </p>
      )}
    </div>
  );
}
